import { ComponentCO2, ComponentCO2Link } from '@acies/domain';
import { Subscription } from 'rxjs';
import CO2Model from '../models/co2-model';
import ListComponentCO2LinkModel from '../models/list-component-co2link-model';
import CO2Service from '../services/co2-service';

// the number of ComponentCO2Links we want to load at once
const BATCH_SIZE = 5000;

export default class ListComponentCO2LinkController {
	public set selectedComponentCO2(value: ComponentCO2 | null) {
		if (this.#selectedComponentCO2 === value) {
			return;
		}
		this.#selectedComponentCO2 = value;
		this.#selectedComponentCO2Changed();
	}

	public get selectedComponentCO2(): ComponentCO2 | null {
		return this.#selectedComponentCO2;
	}

	#selectedComponentCO2: ComponentCO2 | null;
	#listComponentCO2LinkModel: ListComponentCO2LinkModel;
	#co2Service: CO2Service;
	#co2Model: CO2Model;
	#loading: boolean;
	#pendingCO2Selection: boolean;
	#currentOffset: number;
	#loadSubscription: Subscription | null;
	#loadRequestId: number;
	public constructor(co2Model: CO2Model, co2Service: CO2Service) {
		this.#co2Model = co2Model;
		this.#listComponentCO2LinkModel = co2Model.listComponentCO2LinkModel;
		this.#co2Service = co2Service;
		this.#loading = false;
		this.#pendingCO2Selection = false;
		this.#currentOffset = 0;
		this.#loadSubscription = null;
		this.#loadRequestId = 0;
	}

	public clearSelection(): void {
		this.#selectedComponentCO2 = null;
		this.#cancelLoad();
		this.#listComponentCO2LinkModel.co2LinkCollection.clear();
		this.#listComponentCO2LinkModel.loadingSpinnerVisible = false;
		this.#listComponentCO2LinkModel.batchSpinnerVisible = false;
		this.#pendingCO2Selection = false;
		this.#currentOffset = 0;
	}

	public destroy(): void {
		this.#cancelLoad();
	}

	#selectedComponentCO2Changed(): void {
		this.#cancelLoad();
		this.#pendingCO2Selection = false;
		this.#currentOffset = 0;
		this.#listComponentCO2LinkModel.batchSpinnerVisible = false;
		this.#listComponentCO2LinkModel.co2LinkCollection.clear();

		if (this.#selectedComponentCO2 instanceof ComponentCO2) {
			this.#listComponentCO2LinkModel.loadingSpinnerVisible = true;
			this.#loadComponentCO2Links(this.#selectedComponentCO2);
		} else {
			this.#listComponentCO2LinkModel.loadingSpinnerVisible = false;
		}
	}

	#loadComponentCO2Links(value: ComponentCO2): void {
		// start by setting the #loading flag to true,
		// so we can decide what to do when the user selects
		// another componentCO2 or deselects while loading
		this.#loading = true;
		const requestId = this.#loadRequestId;
		this.#loadSubscription = this.#co2Service.listComponentCO2Link(value, this.#currentOffset, BATCH_SIZE).subscribe((response) => {
			if (requestId === this.#loadRequestId) {
				this.#loading = false;
				if (this.#pendingCO2Selection) {
					this.#pendingSelectionWaiting();
				} else if (Array.isArray(response)) {
					this.#componentLinksResponse(value, response);
				} else {
					this.#listComponentCO2LinkModel.loadingSpinnerVisible = false;
					this.#co2Model.showGlobalErrorDialog(response);
				}
			}
		});
	}

	#componentLinksResponse(value: ComponentCO2, response: ComponentCO2Link[]): void {
		this.#listComponentCO2LinkModel.loadingSpinnerVisible = false;
		this.#listComponentCO2LinkModel.co2LinkCollection.addItems(response);
		// if the loaded response is not empty, we load the next batch
		// otherwise we know we are finished and can reset and hide the spinner
		if (response.length > 0) {
			this.#currentOffset += BATCH_SIZE;
			this.#listComponentCO2LinkModel.batchSpinnerVisible = true;
			this.#loadComponentCO2Links(value);
		} else {
			this.#currentOffset = 0;
			this.#listComponentCO2LinkModel.batchSpinnerVisible = false;
		}
	}

	// if we have a componentCO2 change while loading,
	// reset and start loading the newly selected componentCO2 links
	#pendingSelectionWaiting(): void {
		this.#listComponentCO2LinkModel.co2LinkCollection.clear();
		this.#pendingCO2Selection = false;
		this.#currentOffset = 0;
		if (this.#selectedComponentCO2) {
			this.#loadComponentCO2Links(this.#selectedComponentCO2);
		}
	}

	#cancelLoad(): void {
		this.#loadRequestId += 1;
		this.#loadSubscription?.unsubscribe();
		this.#loadSubscription = null;
		this.#loading = false;
	}
}
